class MaterialPicker extends HTMLElement {
	connectedCallback() {
		this.sectionId = this.dataset.section;
		this.location = this.dataset.location || '';

		this.dialog = this.querySelector('[data-material-dialog]');
		this.triggerBtn = this.querySelector('[data-material-open]');
		this.closeBtn = this.querySelector('[data-material-close]');
		this.doneBtn = this.querySelector('[data-material-done]');
		this.cards = Array.from(this.querySelectorAll('[data-material-card]'));
		this.groups = Array.from(this.querySelectorAll('[data-fabric-group]'));
		this.msFilter = this.querySelector('multi-select-filter');
		this.totalCount = Number(this.dataset.totalCount) || this.cards.length;
		this.footerSwatch = this.querySelector('[data-material-footer-swatch]');
		this.footerName = this.querySelector('[data-material-footer-name]');
		this.footerPrice = this.querySelector('[data-material-footer-price]');
		this.triggerImg = this.querySelector('[data-material-trigger-img]');
		this.triggerName = this.querySelector('[data-material-trigger-name]');
		this.triggerSwatchWrap = this.querySelector('.material-picker__swatch');
		this.dialogImages = Array.from(this.querySelectorAll('[data-material-card] img'));
		this.detailsDialog = this.querySelector('[data-material-details-dialog]');
		this.detailsTitle = this.querySelector('[data-material-details-title]');
		this.detailsCopy = this.querySelector('[data-material-details-copy]');

		this.pendingCard = this.cards.find((c) => c.classList.contains('is-selected')) || null;

		this.triggerBtn?.addEventListener('click', () => this.open());
		this.triggerBtn?.addEventListener('pointerenter', () => this.prepareImages(), { once: true });
		this.triggerBtn?.addEventListener('touchstart', () => this.prepareImages(), { once: true, passive: true });
		this.triggerBtn?.addEventListener('focus', () => this.prepareImages(), { once: true });
		this.closeBtn?.addEventListener('click', () => this.close());
		this.dialog?.addEventListener('cancel', (e) => {
			e.preventDefault();
			this.close();
		});
		this.dialog?.addEventListener('click', (e) => {
			if (e.target === this.dialog) this.close();
		});

		this.msFilter?.addEventListener('ms-filter:change', () => this.onFilterChange());
		this.msFilter?.addEventListener('ms-filter:reset', () => this.onFilterChange());
		this.msFilter?.addEventListener('ms-filter:apply', () => this.onFilterChange());

		this.cards.forEach((card) => {
			card.addEventListener('click', () => this.pickCard(card));
		});

		this.doneBtn?.addEventListener('click', () => this.commit());
		this.querySelectorAll('[data-material-read-more]').forEach((button) => {
			button.addEventListener('click', (event) => {
				event.preventDefault();
				event.stopPropagation();
				this.openDetails(button);
			});
		});
		this.querySelectorAll('[data-material-details-close]').forEach((button) => {
			button.addEventListener('click', () => this.closeDetails());
		});
		this.detailsDialog?.addEventListener('cancel', (event) => {
			event.preventDefault();
			this.closeDetails();
		});
		this.detailsDialog?.addEventListener('click', (event) => {
			if (event.target === this.detailsDialog) this.closeDetails();
		});
		this.querySelectorAll('[data-material-details-tab]').forEach((tab) => {
			tab.addEventListener('click', () => this.selectDetailsTab(tab.dataset.materialDetailsTab));
		});

		queueMicrotask(() => this.hydrateFiltersFromUrl());
	}

	open({ instant = false } = {}) {
		if (!this.dialog) return;
		this.prepareImages();
		if (instant) this.dialog.classList.add('no-transition');
		if (this.dialog.showModal) {
			try {
				this.dialog.showModal();
			} catch (_) {
				this.dialog.setAttribute('open', '');
			}
		} else {
			this.dialog.setAttribute('open', '');
		}
		document.body.style.overflow = 'hidden';
		if (instant) {
			requestAnimationFrame(() => {
				requestAnimationFrame(() => this.dialog?.classList.remove('no-transition'));
			});
		}
	}

	close() {
		if (this.dialog?.close) this.dialog.close();
		else this.dialog?.removeAttribute('open');
		document.body.style.overflow = '';
	}

	openDetails(button) {
		if (!this.detailsDialog) return;
		if (this.detailsTitle) this.detailsTitle.textContent = button.dataset.materialDetailTitle || 'Fabric details';
		if (this.detailsCopy) this.detailsCopy.textContent = button.dataset.materialDetailCopy || '';
		this.selectDetailsTab('information');
		try {
			this.detailsDialog.showModal();
		} catch (_) {
			this.detailsDialog.setAttribute('open', '');
		}
	}

	closeDetails() {
		if (this.detailsDialog?.close) this.detailsDialog.close();
		else this.detailsDialog?.removeAttribute('open');
	}

	selectDetailsTab(tabName) {
		this.querySelectorAll('[data-material-details-tab]').forEach((tab) => {
			const active = tab.dataset.materialDetailsTab === tabName;
			tab.classList.toggle('is-active', active);
			tab.setAttribute('aria-selected', String(active));
		});
		this.querySelectorAll('[data-material-details-panel]').forEach((panel) => {
			panel.hidden = panel.dataset.materialDetailsPanel !== tabName;
		});
	}

	prepareImages() {
		if (this.imagesPrepared) return;
		this.imagesPrepared = true;
		this.dialogImages.forEach((image, index) => {
			image.loading = 'eager';
			image.decoding = 'async';
			image.fetchPriority = index < 12 ? 'high' : 'low';
		});
	}

	applyFilters() {
		const { color = [], fabric = [] } = this.currentFilters();
		let matched = 0;
		this.cards.forEach((card) => {
			const cardColorGroups = (card.dataset.colorGroups || '').split('|').filter(Boolean);
			const cardFabricGroups = (card.dataset.fabricGroups || '').split('|').filter(Boolean);
			const matchColor = color.length === 0 || color.some((g) => cardColorGroups.includes(g));
			const matchFabric = fabric.length === 0 || fabric.some((g) => cardFabricGroups.includes(g));
			const show = matchColor && matchFabric;
			card.hidden = !show;
			if (show) matched += 1;
		});
		this.groups.forEach((group) => {
			const visible = Array.from(group.querySelectorAll('[data-material-card]')).some((c) => !c.hidden);
			group.hidden = !visible;
		});
		this.msFilter?.updateCounter(matched, this.totalCount);
	}

	currentFilters() {
		if (this.msFilter) {
			const sel = this.msFilter.getSelections();
			return {
				color: sel.color || [],
				fabric: sel.fabric || [],
			};
		}
		return { color: [], fabric: [] };
	}

	onFilterChange() {
		this.applyFilters();
		MaterialPicker.writeFiltersToUrl(this.currentFilters());
	}

	hydrateFiltersFromUrl() {
		if (!this.msFilter) return;
		const filters = MaterialPicker.readFiltersFromUrl();
		this.msFilter.setSelections(filters, { silent: true });
		this.applyFilters();
	}

	static readFiltersFromUrl() {
		const params = new URLSearchParams(window.location.search);
		const split = (v) => (v ? v.split(',').map((s) => s.trim()).filter(Boolean) : []);
		return {
			color: split(params.get('mp_color')),
			fabric: split(params.get('mp_fabric')),
		};
	}

	static writeFiltersToUrl(filters) {
		const url = new URL(window.location.href);
		MaterialPicker.applyFiltersToUrl(url, filters);
		history.replaceState(history.state, '', url.toString());
	}

	static mergeFiltersIntoUrl(href, filters) {
		const url = new URL(href, window.location.origin);
		MaterialPicker.applyFiltersToUrl(url, filters);
		return url.toString();
	}

	static applyFiltersToUrl(url, filters) {
		const setOrDelete = (key, list) => {
			if (Array.isArray(list) && list.length > 0) url.searchParams.set(key, list.join(','));
			else url.searchParams.delete(key);
		};
		setOrDelete('mp_color', filters.color);
		setOrDelete('mp_fabric', filters.fabric);
	}

	pickCard(card) {
		this.cards.forEach((c) => c.classList.remove('is-selected'));
		card.classList.add('is-selected');
		this.pendingCard = card;
		this.updateFooter(card);
		const url = card.dataset.productUrl;
		if (url) {
			const target = new URL(url, window.location.origin);
			if (target.pathname !== window.location.pathname) {
				const withFilters = MaterialPicker.mergeFiltersIntoUrl(target.href, this.currentFilters());
				this.navigateTo(withFilters, card);
			}
		}
	}

	async navigateTo(href, card, options = {}) {
		if (MaterialPicker._navigating) return;
		MaterialPicker._navigating = true;
		card?.classList.add('is-loading');
		this.classList.add('is-navigating');
		MaterialPicker.showLoading();
		const pushHistory = options.pushHistory !== false;

		const oldWrapper = document.getElementById(`shopify-section-${this.sectionId}`);
		const wantedLocation = this.location;
		const wasOpen = !!this.dialog?.open;
		const lastFilters = this.currentFilters();

		try {
			const requestUrl = new URL(href, window.location.origin);
			if (this.sectionId) requestUrl.searchParams.set('section_id', this.sectionId);
			const res = await fetch(requestUrl, { headers: { Accept: 'text/html' } });
			if (!res.ok) throw new Error(`page ${res.status}`);
			const html = await res.text();

			const doc = new DOMParser().parseFromString(html, 'text/html');

			const newWrapper =
				(this.sectionId && doc.getElementById(`shopify-section-${this.sectionId}`)) ||
				doc.querySelector('material-picker')?.closest('.shopify-section');

			if (!newWrapper) throw new Error('new section not found in target page');

			const targetWrapper = oldWrapper || document.querySelector('material-picker')?.closest('.shopify-section');
			if (!targetWrapper) throw new Error('current section wrapper missing');

			const modelTitle = doc.querySelector('model-picker[data-product-page-title]')?.dataset.productPageTitle?.trim();
            if (modelTitle || doc.title) document.title = modelTitle || doc.title;
			if (pushHistory) history.pushState({ materialPicker: true }, '', href);

			targetWrapper.innerHTML = newWrapper.innerHTML;
			if (targetWrapper.id !== newWrapper.id) targetWrapper.id = newWrapper.id;
			MaterialPicker.runScripts(targetWrapper);

			requestAnimationFrame(() => {
				const newPicker =
					targetWrapper.querySelector(
						`material-picker[data-location="${wantedLocation}"]`,
					) || targetWrapper.querySelector('material-picker');
				if (newPicker) {
					newPicker.msFilter?.setSelections(lastFilters, { silent: true });
					newPicker.applyFilters();
					if (wasOpen) newPicker.open({ instant: true });
				}
				document.dispatchEvent(
					new CustomEvent('material-picker:navigated', { detail: { url: href } }),
				);
			});
		} catch (err) {
			console.error('[material-picker] navigation failed, falling back to full reload', err);
			window.location.href = href;
		} finally {
			MaterialPicker._navigating = false;
			MaterialPicker.hideLoading();
		}
	}

	static showLoading() {
		if (document.getElementById('material-picker-loading')) return;
		const overlay = document.createElement('div');
		overlay.id = 'material-picker-loading';
		overlay.className = 'material-picker-loading';
		overlay.innerHTML = '<div class="material-picker-loading__spinner" role="status" aria-label="Loading"></div>';
		document.body.appendChild(overlay);
	}

	static hideLoading() {
		document.getElementById('material-picker-loading')?.remove();
	}

	static runScripts(root) {
		root.querySelectorAll('script').forEach((oldScript) => {
			if (oldScript.src) return;
			const newScript = document.createElement('script');
			for (const attr of oldScript.attributes) newScript.setAttribute(attr.name, attr.value);
			newScript.textContent = oldScript.textContent;
			oldScript.parentNode.replaceChild(newScript, oldScript);
		});
	}

	updateFooter(card) {
		if (this.footerSwatch) {
			const url = card.dataset.swatch;
			this.footerSwatch.innerHTML = url ? `<img src="${url}" alt="">` : '';
		}
		if (this.footerName) this.footerName.textContent = card.dataset.nameFull || card.dataset.name || '';
		if (this.footerPrice) this.footerPrice.textContent = card.dataset.price || '';
	}

	updateTrigger(card) {
		if (!card) return;
		if (this.triggerName) this.triggerName.textContent = card.dataset.name || '';
		const url = card.dataset.swatch;
		if (url) {
			if (this.triggerImg) {
				this.triggerImg.src = url;
				this.triggerImg.alt = card.dataset.name || '';
			} else if (this.triggerSwatchWrap) {
				this.triggerSwatchWrap.innerHTML = `<img src="${url}" alt="${card.dataset.name || ''}" loading="lazy" data-material-trigger-img>`;
				this.triggerImg = this.triggerSwatchWrap.querySelector('img');
			}
		}
	}

	commit() {
		if (!this.pendingCard) {
			this.close();
			return;
		}
		const url = this.pendingCard.dataset.productUrl;
		if (url) {
			const target = new URL(url, window.location.origin);
			if (target.pathname !== window.location.pathname) {
				const withFilters = MaterialPicker.mergeFiltersIntoUrl(target.href, this.currentFilters());
				this.navigateTo(withFilters, this.pendingCard);
				return;
			}
		}
		this.close();
	}
}

if (!customElements.get('material-picker')) {
	customElements.define('material-picker', MaterialPicker);
}

if (!window.__materialPickerPopstateBound) {
	window.__materialPickerPopstateBound = true;
	window.addEventListener('popstate', () => {
		const picker = document.querySelector('material-picker');
		if (!picker) {
			window.location.reload();
			return;
		}
		picker.navigateTo(window.location.href, null, { pushHistory: false }).catch(() => window.location.reload());
	});
}
